import React, { useState } from "react";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { Checkbox, CircularProgress } from "@mui/material";
import SocialIcon from "./SocialIcon";
import { ReactComponent as InstaIcon } from "../../../assets/svg/instagram.svg";
import { ReactComponent as LinkedinIcon } from "../../../assets/svg/linkedin.svg";
import { ReactComponent as FacebookIcon } from "../../../assets/svg/facebook.svg";
import { Button } from "../../../components/Button/Button";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { IconUser, IconPlus } from "../../../utils/icons";
import { formatProfessionsWithExperience } from "../../../utils/professionExperience";
import { describeSharedSiblings, SHOT_SELECTION_STATUSES, getBpp } from "../helper";
import { ExpertIdPill } from "../../../components/ExpertIdPill/ExpertIdPill";
import { ExpertKeyPointsList } from "../../../components/ExpertKeyPoints/ExpertKeyPoints";
import { ExpertKeyPointsModal } from "../../../components/ExpertKeyPointsModal/ExpertKeyPointsModal";
import { ConfirmationDialog } from "../../../components/ConfirmationDialog/ConfirmationDialog";
import {
  changeSelectionRoster,
  rosterErrorMessage,
} from "../../../utils/selectionRoster";
import { ScoreRatePills } from "./ScoreRatePills";
import { ExpertOptStatusBar } from "./ExpertOptStatusBar";
import { AlgoScorePerProduct } from "./AlgoScorePerProduct";
import ExpertContactPopover from "./ExpertContactPopover";
import { cldUrl } from "../../../utils/cloudinary";
import { useUser } from "../../../hooks/useUser";
import { isInternal } from "../../../utils";

const ExpertSelectionList = ({
  recommendationCheck,
  pros,
  onUpdate,
  project,
  selectedExperts = [],
  setSelectedExperts = undefined,
  isApprovalPage = false,
  algoProducts = [],
  algoScores = {},
  algoExpertTypes = {},
  // Other projects sharing this project's selection process; when non-empty,
  // removing an expert here also removes them from those projects.
  sharedSiblingProjects = [],
  onRequestReopen = /** @type {any} */ (undefined),
}) => {
  const [isAddingToProject, setIsAddingToProject] = useState(false);
  const [showKeyPointsModal, setShowKeyPointsModal] = useState(false);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const { role } = useUser();

  if (!pros) return null;

  const { avatar, displayName, description, professions, userid, selected } =
    pros;
  const navigate = useNavigate();
  const projectBio = pros.projects?.[0]?.BrandProjectPro?.project_bio?.trim() || '';
  const isChecked = selectedExperts.includes(userid);
  // Locked selection (at/past Pre-Production): roster is frozen.
  const processLocked = !!project?.process_locked;
  const selectionShot = SHOT_SELECTION_STATUSES.includes(project?.status);
  const canReopenSelection =
    processLocked && !selectionShot && !!onRequestReopen;
  const checkboxNumber = isChecked ? selectedExperts.indexOf(userid) + 1 : null;
  const showContact =
    isInternal(role) && (recommendationCheck || selected || isChecked);

  const handleCheckboxToggle = () => {
    if (!setSelectedExperts) return;

    if (isChecked) {
      setSelectedExperts((prev) => prev.filter((id) => id !== userid));
    } else {
      setSelectedExperts((prev) => [...prev, userid]);
    }
  };

  const professionNames = formatProfessionsWithExperience(professions);

  const performSelectChange = async (isChecked) => {
    const brandId = project?.brands?.find((b) => b.rmn !== true)?.id;

    setIsAddingToProject(true);

    try {
      await changeSelectionRoster(
        isChecked ? "add" : "remove",
        brandId,
        project?.id,
        userid
      );
      onUpdate();
    } catch (error) {
      console.error("Error updating expert selection:", error);
      toast.error(rosterErrorMessage(error));
    } finally {
      setIsAddingToProject(false);
    }
  };

  const handleSelectChange = async (e) => {
    const isChecked = e.target.checked;

    if (canReopenSelection) {
      const bpp = getBpp(pros);
      const isConfirmedExpert =
        bpp?.internal_confirmed === true || bpp?.user_confirmed === true;
      // Removing an UNCONFIRMED expert from a committed selection is quiet
      // bench hygiene — the API drops the row and just logs it, no rewind —
      // so only adds and confirmed-expert removals need the reopen
      // confirmation notice.
      if (isChecked || isConfirmedExpert) {
        onRequestReopen({
          userid,
          displayName: displayName || "this expert",
          action: isChecked ? "add" : "remove",
        });
        return;
      }
    }

    // Removal is process-wide: confirm when the process spans other projects.
    if (!isChecked && sharedSiblingProjects.length > 0) {
      setShowRemoveConfirm(true);
      return;
    }

    await performSelectChange(isChecked);
  };

  return (
    <>
      {/* The arbitrary shadow value needs its explicit "shadow:" prefix — without
          it Tailwind reads the value as a shadow COLOUR and emits no box-shadow
          at all. --sh-1 lifts the card off the canvas, which the border alone
          could not do in light (white on #F4F6FA is only 1.08).
          NB: do not write the bracketed class form in a comment — the content
          scanner extracts it and emits a junk rule. */}
      <div className="rounded-[16px] border border-border-token bg-surface shadow-[shadow:var(--sh-1)] w-full min-w-0 overflow-hidden">
        <ExpertOptStatusBar expert={pros} project={project} onSigned={onUpdate} />
        <div className="flex flex-col gap-4 p-4">
          <div className="flex flex-col sm:flex-row items-start">
            {avatar ? (
              <img
                className="w-[114px] h-[114px] min-w-[114px] min-h-[114px] object-cover object-top rounded-[6px] mb-4 sm:mb-0 sm:mr-4"
                src={cldUrl(avatar, { w: 228, h: 228, crop: "fill", gravity: "face" })}
                alt={displayName || "Expert"}
              />
            ) : (
              <div className="w-[114px] h-[114px] min-w-[114px] min-h-[114px] flex shrink-0 items-center justify-center rounded-[6px] bg-tint mb-4 sm:mb-0 sm:mr-4">
                <IconUser width={32} height={32} />
              </div>
            )}
            <div className="flex flex-col w-full gap-3">
              <div className="flex items-start justify-between w-full">
                <div className="flex flex-col gap-3">
                  <div className="flex items-center gap-[8px] text-[14px] font-[450] text-text-2 font-circular">
                    <div>{professionNames}</div>
                  </div>
                  {algoProducts.length > 0 && (
                    <AlgoScorePerProduct
                      products={algoProducts}
                      scores={algoScores || {}}
                      expertTypes={algoExpertTypes || {}}
                      expertName={displayName || ""}
                    />
                  )}
                  {!recommendationCheck && (
                    <hr className="w-full border-t border-border-token" />
                  )}
                </div>

                <div className="flex items-center gap-2">
                  {recommendationCheck && !isApprovalPage ? (
                    <Checkbox
                      icon={<RadioButtonUncheckedIcon />}
                      checkedIcon={
                        <div className="w-[24px] h-[24px] rounded-full bg-accent text-white text-[12px] flex items-center justify-center">
                          {checkboxNumber}
                        </div>
                      }
                      checked={isChecked}
                      onChange={handleCheckboxToggle}
                      sx={{
                        backgroundColor: "var(--surface) !important",
                        padding: "4px",
                        color: "#DFDEDE",
                        "&.Mui-checked": {
                          color: "var(--accent)",
                        },
                      }}
                    />
                  ) : !isApprovalPage ? (
                    <div className="relative">
                      {isAddingToProject && (
                        <div className="absolute inset-0 flex items-center justify-center bg-[rgba(255,255,255,0.8)] dark:bg-[rgba(24,33,47,0.8)] rounded z-10">
                          <CircularProgress size={16} sx={{ color: "var(--accent)" }} />
                        </div>
                      )}
                      <Checkbox
                        icon={<RadioButtonUncheckedIcon />}
                        checkedIcon={<CheckCircleIcon />}
                        checked={selected}
                        onChange={handleSelectChange}
                        disabled={
                          isAddingToProject ||
                          (processLocked && !canReopenSelection)
                        }
                        sx={{
                          backgroundColor: "var(--surface) !important",
                          padding: "4px",
                          color: "#DFDEDE",
                          "&.Mui-checked": {
                            color: "var(--accent)",
                          },
                        }}
                      />
                    </div>
                  ) : null}
                </div>
              </div>

              <div className="flex flex-wrap items-center justify-between gap-2 w-full">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="overflow-hidden text-ellipsis whitespace-nowrap text-[18px] font-semibold leading-[132%] text-text-1 font-circular max-w-[180px]">
                    {displayName || "Unnamed Expert"}
                  </span>
                  <ExpertIdPill userid={userid} />
                  {!recommendationCheck && (
                    <span
                      className="text-[14px] font-extrabold text-text-1 cursor-pointer"
                      onClick={() =>
                        navigate(
                          `/experts/${pros.userid}/${pros.slug}?proId=${pros.userid}${project?.id ? `&project=${project.id}` : ''}`
                        )
                      }
                    >
                      View Profile
                    </span>
                  )}
                </div>
                <div className="flex gap-2 items-center">
                  {showContact && !recommendationCheck && (
                    <ExpertContactPopover pro={pros} project={project} />
                  )}
                  {pros.instagram && <SocialIcon icon={<InstaIcon />} />}
                  {pros.linkedin && <SocialIcon icon={<LinkedinIcon />} />}
                  {pros.facebook && <SocialIcon icon={<FacebookIcon />} />}
                  {isApprovalPage && (
                    <div
                      className="rounded-[100px] bg-tint p-[6px] cursor-pointer hover:bg-[#E8DCC8] dark:hover:bg-track transition-colors"
                      onClick={() => setShowKeyPointsModal(true)}
                      title="Add project-specific key points"
                    >
                      <IconPlus
                        width={16}
                        height={16}
                        stroke="#AB854A"
                        strokeWidth={4.5}
                      />
                    </div>
                  )}
                </div>
              </div>

              {recommendationCheck && (
                <div>
                  <hr className="w-full border-t border-border-token" />
                  <div className="flex items-center justify-between gap-2 min-w-0 mt-3">
                    <div className="min-w-0 flex-1">
                      <ScoreRatePills expert={pros} showRequired />
                    </div>
                    {showContact && (
                      <div className="shrink-0">
                        <ExpertContactPopover pro={pros} project={project} />
                      </div>
                    )}
                  </div>
                </div>
              )}

              {(projectBio || description) && (
                <div
                  className="text-[12px] font-[450] text-text-2 overflow-hidden"
                  style={{
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {projectBio || description}
                </div>
              )}

              <ExpertKeyPointsList
                expert={pros}
                project={project}
                showProjectSpecific={true}
              />
            </div>
          </div>

          {(recommendationCheck || isApprovalPage) && (
            <Button
              variant="soft"
              color="default"
              className="w-full"
              onClick={() =>
                navigate(
                  `/experts/${pros.userid}/${pros.slug}?proId=${pros.userid}${project?.id ? `&project=${project.id}` : ''}`
                )
              }
            >
              View Profile
            </Button>
          )}
        </div>
      </div>

      {showKeyPointsModal && (
        <ExpertKeyPointsModal
          open={showKeyPointsModal}
          onClose={() => setShowKeyPointsModal(false)}
          expert={pros}
          project={project}
          onUpdate={onUpdate}
        />
      )}

      <ConfirmationDialog
        open={showRemoveConfirm}
        onClose={() => setShowRemoveConfirm(false)}
        onConfirm={() => {
          setShowRemoveConfirm(false);
          performSelectChange(false);
        }}
        title="Remove Expert"
        description={`Removing ${displayName || "this expert"} also removes them from ${describeSharedSiblings(sharedSiblingProjects)}, which share this project's expert selection.`}
        confirmLabel="Remove"
        cancelLabel="Cancel"
      />
    </>
  );
};

export default ExpertSelectionList;
